import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, CardBody, CardHeader, Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, Button, Spinner, Input, Alert, Badge,
} from 'reactstrap';
import { motion } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import PermissionGuard from '../../components/Common/PermissionGuard';
import { DOCUMENT_VAULT_CREATE, DOCUMENT_VAULT_DELETE } from '../../constants/permissions';
import AddDocumentModal from './AddDocumentModal';

const PAGE_SIZE = 20;

/**
 * Knowledge Hub — central documents view. Reads from /documents/get-all (the new
 * generic documents table) and surfaces every file uploaded anywhere in the product:
 * Hub-direct uploads + module attachments projected by each module's service
 * (assets, vendors, work orders, …) via the eager-aggregation hook.
 *
 * Read-mostly: rows are clickable for a detail drawer; deletion is permission-gated.
 * Certificates live under Compliance and surface here only when a generic doc
 * happens to be linked to one (sourceModule=CERTIFICATE).
 */
const Documents = () => {
  const { t } = useTranslation();

  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [count, setCount] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  document.title = `${t('knowledge_hub.documents.title', 'Documents')} | Arcmedix`;

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setApiError('');
    try {
      const params = { count };
      if (searchTerm.trim()) params.searchTerm = searchTerm.trim();
      if (moduleFilter) params.sourceModule = moduleFilter;
      const { data } = await api.get('documents/get-all', { params });
      const list = data?.response || [];
      setDocuments(list);
      setHasMore(list.length >= count);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setApiError(t('knowledge_hub.documents.load_error', 'Could not load documents.'));
    } finally {
      setLoading(false);
    }
  }, [count, searchTerm, moduleFilter, t]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleDelete = async () => {
    if (!selectedDoc?.id) return;
    setLoading(true);
    setApiError('');
    try {
      await api.delete(`documents/delete/${selectedDoc.id}`);
      await fetchDocuments();
      setDeleteOpen(false);
      setSelectedDoc(null);
    } catch (err) {
      console.error('Error deleting document:', err);
      setApiError(err?.response?.data?.error || t('knowledge_hub.documents.delete_failed', 'Could not delete document.'));
    } finally {
      setLoading(false);
    }
  };

  const moduleLabel = (sourceModule) => {
    if (!sourceModule) return null;
    const key = `knowledge_hub.modules.${sourceModule.toLowerCase()}`;
    return t(key, prettyModule(sourceModule));
  };

  const sourceDisplay = (doc) => {
    if (!doc.sourceModule || doc.sourceModule === 'HUB') {
      return <span className="text-muted">{t('knowledge_hub.source.hub', 'Hub upload')}</span>;
    }
    const moduleName = moduleLabel(doc.sourceModule);
    const entityName = doc.sourceEntityName;
    return (
      <div className="d-flex flex-column">
        <Badge color="info" pill className="align-self-start mb-1" style={{ fontWeight: 500 }}>
          {moduleName}
        </Badge>
        {entityName && <span className="small text-muted">{entityName}</span>}
      </div>
    );
  };

  const moduleOptions = useMemo(() => ([
    { value: '', label: t('knowledge_hub.filter.all_modules', 'All modules') },
    { value: 'HUB',             label: t('knowledge_hub.modules.hub',             'Hub upload') },
    { value: 'CERTIFICATE',     label: t('knowledge_hub.modules.certificate',     'Certificate') },
    { value: 'CONTRACT',        label: t('knowledge_hub.modules.contract',        'Contract') },
    { value: 'ASSET',           label: t('knowledge_hub.modules.asset',           'Asset') },
    { value: 'VENDOR',          label: t('knowledge_hub.modules.vendor',          'Vendor') },
    { value: 'WORK_ORDER',      label: t('knowledge_hub.modules.work_order',      'Work Order') },
    { value: 'SERVICE_REQUEST', label: t('knowledge_hub.modules.service_request', 'Service Request') },
    { value: 'METER',           label: t('knowledge_hub.modules.meter',           'Meter') },
    { value: 'SPARE_PART',      label: t('knowledge_hub.modules.spare_part',      'Spare Part') },
    { value: 'TOOL',            label: t('knowledge_hub.modules.tool',            'Tool') },
  ]), [t]);

  return (
    <div className="page-content mt-4">
      <div className="container-fluid">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3 flex-wrap"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission={DOCUMENT_VAULT_CREATE}>
            <button
              type="button"
              className="btn btn-soft-success waves-effect waves-light d-flex align-items-center gap-2"
              onClick={() => setAddModalOpen(true)}
            >
              <FeatherIcon icon="plus" size={18} />
              {t('knowledge_hub.documents.add', 'Add Document')}
            </button>
          </PermissionGuard>

          <div style={{ minWidth: 280, flexGrow: 1, maxWidth: 420 }}>
            <Input
              type="text"
              placeholder={t('knowledge_hub.documents.search_placeholder', 'Search by document name…')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={{ minWidth: 220 }}>
            <Input type="select" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)}>
              {moduleOptions.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
              ))}
            </Input>
          </div>
        </motion.div>

        {apiError && <Alert color="danger" className="mb-3">{apiError}</Alert>}

        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">
              {t('knowledge_hub.documents.title', 'Documents')}
            </h3>
          </CardHeader>
          <CardBody>
            <div
              className="table-responsive"
              style={{ overflowX: 'auto', whiteSpace: 'nowrap', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}
            >
              <table className="table table-hover mb-0" style={{ borderSpacing: '0.1rem', borderCollapse: 'separate' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                  <tr>
                    <th>#</th>
                    <th>{t('knowledge_hub.documents.column.name',         'Document name')}</th>
                    <th>{t('knowledge_hub.documents.column.source',       'Source')}</th>
                    <th>{t('knowledge_hub.documents.column.files',        'Files')}</th>
                    <th>{t('knowledge_hub.documents.column.date_added',   'Date added')}</th>
                    <th className="text-end">{t('shared.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && documents.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-4"><Spinner size="sm" /></td></tr>
                  )}
                  {!loading && documents.length === 0 && (
                    <tr><td colSpan={6} className="text-center text-muted py-4">
                      {t('knowledge_hub.documents.empty', 'No documents yet.')}
                    </td></tr>
                  )}
                  {documents.map((doc, idx) => (
                    <tr key={doc.id} style={{ cursor: 'pointer' }} onClick={() => { setSelectedDoc(doc); setDetailsOpen(true); }}>
                      <td className="fw-bold">{idx + 1}</td>
                      <td>
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); setSelectedDoc(doc); setDetailsOpen(true); }}
                          className="text-decoration-none"
                        >
                          {doc.name || t('knowledge_hub.documents.unnamed', '(unnamed)')}
                        </a>
                      </td>
                      <td>{sourceDisplay(doc)}</td>
                      <td>
                        <FeatherIcon icon="paperclip" size={14} className="me-1" />
                        {(doc.documentUrls || []).length}
                      </td>
                      <td>{formatDate(doc.createdAt)}</td>
                      <td className="text-end" onClick={(e) => e.stopPropagation()}>
                        <PermissionGuard permission={DOCUMENT_VAULT_DELETE}>
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger"
                            onClick={() => { setSelectedDoc(doc); setDeleteOpen(true); }}
                            title={t('shared.delete', 'Delete')}
                          >
                            <FeatherIcon icon="trash-2" size={16} />
                          </button>
                        </PermissionGuard>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-center text-muted mt-3">
              {hasMore ? (
                <button
                  className="btn btn-link"
                  onClick={() => setCount((c) => c + PAGE_SIZE)}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : t('shared.load_more', 'Load more')}
                </button>
              ) : (
                documents.length > 0 && t('knowledge_hub.documents.no_more', 'No more documents to load')
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Detail modal — matches the gradient-header pattern used by Certificate Details. */}
      <Modal
        isOpen={detailsOpen}
        toggle={() => setDetailsOpen(false)}
        size="lg"
        className="modal-dialog-centered"
      >
        <div
          className="modal-header border-0"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: '1.5rem 2rem',
          }}
        >
          <div className="w-100">
            <div className="d-flex justify-content-between align-items-start">
              <div className="text-white">
                <h4 className="mb-2 fw-bold">
                  <i className="bx bx-file-blank me-2"></i>
                  {selectedDoc?.name || t('knowledge_hub.documents.unnamed', '(unnamed)')}
                </h4>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {selectedDoc?.sourceModule && selectedDoc.sourceModule !== 'HUB' && (
                    <span className="badge bg-white bg-opacity-25 text-white">
                      {moduleLabel(selectedDoc.sourceModule)}
                    </span>
                  )}
                  {selectedDoc?.sourceEntityName && (
                    <span className="badge bg-white bg-opacity-25 text-white">
                      {selectedDoc.sourceEntityName}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={() => setDetailsOpen(false)}
                aria-label={t('shared.close', 'Close')}
              />
            </div>
          </div>
        </div>
        <ModalBody className="p-4">
          {selectedDoc && (
            <Row className="g-4">
              <Col md={6}>
                <label className="text-muted small mb-1 d-block">
                  {t('knowledge_hub.documents.column.source', 'Source')}
                </label>
                <div>{sourceDisplay(selectedDoc)}</div>
              </Col>
              <Col md={6}>
                <label className="text-muted small mb-1 d-block">
                  {t('knowledge_hub.documents.column.date_added', 'Date added')}
                </label>
                <div className="fw-medium">{formatDate(selectedDoc.createdAt)}</div>
              </Col>
              <Col md={12}>
                <label className="text-muted small mb-2 d-block">
                  {t('knowledge_hub.documents.column.files', 'Files')}
                </label>
                {(selectedDoc.documentUrls || []).length === 0 ? (
                  <div className="text-muted">{t('knowledge_hub.documents.no_files', 'No files.')}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {(selectedDoc.documentUrls || []).map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="d-flex align-items-center bg-light rounded px-3 py-2 text-decoration-none"
                      >
                        <FeatherIcon icon="file" size={16} className="text-primary me-2" />
                        <span className="text-truncate">{extractFileName(url) || url}</span>
                      </a>
                    ))}
                  </div>
                )}
              </Col>
              {selectedDoc.notes && (
                <Col md={12}>
                  <label className="text-muted small mb-1 d-block">
                    {t('knowledge_hub.documents.notes', 'Notes')}
                  </label>
                  <div>{selectedDoc.notes}</div>
                </Col>
              )}
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDetailsOpen(false)}>
            {t('shared.close', 'Close')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete confirm */}
      <Modal isOpen={deleteOpen} toggle={() => setDeleteOpen(false)} centered>
        <ModalHeader toggle={() => setDeleteOpen(false)}>
          {t('knowledge_hub.documents.confirm_delete_title', 'Delete document')}
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">{t('knowledge_hub.documents.confirm_delete', 'Delete this document from the vault?')}</p>
          {selectedDoc?.name && <p className="mt-2 fw-semibold">{selectedDoc.name}</p>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteOpen(false)} disabled={loading}>
            {t('shared.cancel', 'Cancel')}
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            {loading ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </Button>
        </ModalFooter>
      </Modal>

      <AddDocumentModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreated={async () => {
          await fetchDocuments();
          setAddModalOpen(false);
        }}
      />
    </div>
  );
};

function prettyModule(m) {
  switch (m) {
    case 'HUB': return 'Hub upload';
    case 'CERTIFICATE': return 'Certificate';
    case 'CONTRACT': return 'Contract';
    case 'ASSET': return 'Asset';
    case 'VENDOR': return 'Vendor';
    case 'WORK_ORDER': return 'Work Order';
    case 'SERVICE_REQUEST': return 'Service Request';
    case 'METER': return 'Meter';
    case 'SPARE_PART': return 'Spare Part';
    case 'TOOL': return 'Tool';
    case 'SUB_PREVENTIVE_WO': return 'Preventive WO';
    default: return m;
  }
}

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function extractFileName(url) {
  if (!url) return null;
  const slash = url.lastIndexOf('/');
  return slash >= 0 ? url.substring(slash + 1) : url;
}

export default Documents;
